import React, {
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react';
import IOffersRoundContext, {
    IErrors,
    IOfferLists,
} from './interfaces/IOffersRoundContext';
import IOffer from '../../../common/interfaces/offers/IOffer';
import { useToast } from '../toast/toast.context';
import RoundManagerApiService from '../../../common/services/roundmanager-api';
import Position from '../../../common/enums/position';
import {
    INITIAL_PAGE_NUMBER,
    PAGE_SIZE,
    SELECTION_LIMIT,
} from '../../../pages/smartcargas/rounds/constants/rounds.constants';
import i18n from '../../../lib/i18n';
import IApiResponse from '../../../common/interfaces/IApiResponse';
import ILoadsFiltersModel from '../../../common/interfaces/IFiltersModel';
import { useConnection } from '../connection/connection-context';
import roundReducer, { IRoundState } from '../../reducers/round/round-reducer';
import { IOfferLoadRateByVehicle } from '../../../common/interfaces/offers/IOfferLoad';
import { getAcceptedRateByVehicle } from '../../../pages/smartcargas/rounds/utils/offers-utils';

export const OffersRoundContext = React.createContext<IOffersRoundContext>(
    {} as IOffersRoundContext,
);

export function OffersRoundProvider({ children }: Readonly<PropsWithChildren>) {
    const { setToast } = useToast();
    const { connection, isConnected, connectionState } = useConnection();
    const previousConnectionState = useRef(connectionState);

    const [state, dispatch] = useReducer(roundReducer, {
        status: 'idle',
    } as IRoundState);

    const [acceptanceOffersList, setAcceptanceOffersList] = useState<IOffer[]>(
        [],
    );
    const [acceptedOffersRates, setAcceptedOffersRates] = useState<{
        [offerId: string]: IOfferLoadRateByVehicle;
    }>({});

    const hydrateAcceptedOffersRates = useCallback((offers: IOffer[]) => {
        const rates = offers.reduce<{
            [offerId: string]: IOfferLoadRateByVehicle;
        }>((current, offer) => {
            const acceptedRate = getAcceptedRateByVehicle(offer);

            if (acceptedRate) {
                current[offer.loadOfferId] = acceptedRate;
            }

            return current;
        }, {});

        if (Object.keys(rates).length > 0) {
            setAcceptedOffersRates(current => ({ ...current, ...rates }));
        }
    }, []);
    const [confirmationOffersList, setConfirmationOffersList] =
        useState<IOfferLists>({
            [Position.First]: [],
            [Position.Second]: [],
            [Position.Third]: [],
        });

    const [roundError, setRoundError] = useState<string | undefined>();
    const [acceptanceError, setAcceptanceError] = useState<
        string | undefined
    >();
    const [confirmationErrors, setConfirmationErrors] = useState<IErrors>({});

    const [isConfirmationAvailable, setIsConfirmationAvailable] = useState({
        [Position.First]: true,
        [Position.Second]: false,
        [Position.Third]: false,
    });

    const availableCount = useRef(0);
    const effectedCount = useRef(0);

    const acceptancePages = useRef(0);
    const acceptanceHasMore = useRef(true);

    const confirmationPages = useRef({
        [Position.First]: 0,
        [Position.Second]: 0,
        [Position.Third]: 0,
    });

    const confirmationHasMore = useRef({
        [Position.First]: true,
        [Position.Second]: true,
        [Position.Third]: true,
    });

    const filters = useRef<ILoadsFiltersModel>({} as ILoadsFiltersModel);

    const selectedOffersCount = useMemo(() => {
        let selectedSum = 0;
        for (let i = Position.First; i <= Position.Third; i++) {
            selectedSum += confirmationOffersList[i].filter(
                x => x.checked,
            ).length;
        }

        selectedSum += acceptanceOffersList.filter(x => x.checked).length;

        return selectedSum;
    }, [confirmationOffersList, acceptanceOffersList]);

    const updateEffectedCounters = useCallback(
        (increment: boolean = true) => {
            if (increment) {
                effectedCount.current++;
            }

            if (!increment && effectedCount.current > 0) {
                effectedCount.current--;
            }
        },
        [effectedCount],
    );

    const fetchRoundInfo = useCallback(async () => {
        const { response, error } =
            await RoundManagerApiService.Offers.getRoundInfoAsync();

        if (response) {
            filters.current = {} as ILoadsFiltersModel;
            effectedCount.current = response.effectedCount;
            availableCount.current = response.availableCount;

            dispatch({ type: 'FETCH_PHASE_SUCCESS', payload: response });

            return true;
        }

        dispatch({ type: 'ERROR', payload: error?.cause as string });

        switch (error?.cause) {
            case 'RoundNotFoundException':
            case 'CarrierNotFoundException':
                setRoundError(error?.cause);
                break;
            default:
                setRoundError('Error');
                setToast('negative', i18n.t('Erro'), error?.message);
                break;
        }

        return false;
    }, [setToast]);

    const fetchAcceptanceOffers = useCallback(
        async (page: number) => {
            const { response, error } =
                await RoundManagerApiService.Offers.getOffersAvailableV2Async(
                    filters.current,
                    page,
                    PAGE_SIZE,
                );

            if (error) {
                setAcceptanceError('Error');
                setToast('negative', i18n.t('Erro'), error.message);
                acceptanceHasMore.current = false;

                dispatch({ type: 'ERROR', payload: error.message });

                return;
            }

            hydrateAcceptedOffersRates(response!.data);
            dispatch({ type: 'FETCH_OFFERS_SUCCESS', payload: response! });

            acceptanceHasMore.current =
                acceptancePages.current < response!.totalPages;
        },
        [hydrateAcceptedOffersRates, setToast],
    );

    const fetchConfirmationOffersByPosition = useCallback(
        async (position: Position, page: number) => {
            const { response, error } =
                await RoundManagerApiService.Offers.getOffersAvailableV2Async(
                    filters.current,
                    page,
                    PAGE_SIZE,
                    position,
                );

            if (response) {
                setConfirmationErrors(current => ({
                    ...current,
                    [position]: undefined,
                }));
                confirmationHasMore.current[position] =
                    confirmationPages.current[position] < response.totalPages;

                hydrateAcceptedOffersRates(response.data);

                dispatch({
                    type: 'FETCH_OFFERS_SUCCESS',
                    payload: response,
                    position,
                });
            } else {
                setConfirmationErrors(s => ({ ...s, [position]: 'Error' }));
                setToast('negative', i18n.t('Erro'), error?.message);
                confirmationHasMore.current[position] = false;

                dispatch({ type: 'ERROR', payload: error!.message });
            }
        },
        [hydrateAcceptedOffersRates, setToast],
    );

    const fetchFirstPageByPosition = useCallback(
        async (position: Position) => {
            dispatch({ type: 'FETCH_OFFERS_START', position });

            if (state.currentPhase === 'Acceptance') {
                acceptancePages.current = INITIAL_PAGE_NUMBER;
                await fetchAcceptanceOffers(INITIAL_PAGE_NUMBER);

                return;
            }

            confirmationPages.current[position] = INITIAL_PAGE_NUMBER;
            await fetchConfirmationOffersByPosition(
                position,
                INITIAL_PAGE_NUMBER,
            );
        },
        [
            fetchConfirmationOffersByPosition,
            fetchAcceptanceOffers,
            state.currentPhase,
        ],
    );

    const fetchNextPageByPosition = useCallback(
        async (position: Position) => {
            if (state.currentPhase === 'Acceptance') {
                acceptancePages.current++;

                if (
                    acceptancePages.current === INITIAL_PAGE_NUMBER ||
                    acceptanceHasMore.current
                ) {
                    await fetchAcceptanceOffers(acceptancePages.current);
                }

                return;
            }

            confirmationPages.current[position]++;

            if (
                confirmationPages.current[position] === INITIAL_PAGE_NUMBER ||
                confirmationHasMore.current[position]
            ) {
                await fetchConfirmationOffersByPosition(
                    position,
                    confirmationPages.current[position],
                );
            }
        },
        [
            fetchConfirmationOffersByPosition,
            fetchAcceptanceOffers,
            state.currentPhase,
        ],
    );

    const processAcceptance = useCallback(
        async (
            offerId: string,
            processAcceptanceFunction: (
                loadOfferId: string,
            ) => Promise<IApiResponse<boolean>>,
            isAccept: boolean,
            errorMessage: string,
        ) => {
            const { response } = await processAcceptanceFunction(offerId);

            if (response) {
                setAcceptanceOffersList(prevItems =>
                    prevItems.map((item: IOffer) =>
                        item.loadOfferId === offerId
                            ? { ...item, accepted: isAccept }
                            : item,
                    ),
                );

                updateEffectedCounters(isAccept);
                return true;
            } else {
                setToast('negative', i18n.t('Erro'), errorMessage);
                return false;
            }
        },
        [setToast, updateEffectedCounters],
    );

    const acceptOffer = useCallback(
        async (offerId: string, vehicleName: string) => {
            return processAcceptance(
                offerId,
                (id: string) =>
                    RoundManagerApiService.Offers.acceptOfferAsync(
                        id,
                        vehicleName,
                    ),
                true,
                i18n.t('SmartCargas.Rounds.ErrorAcceptingOffer'),
            );
        },
        [processAcceptance],
    );

    const cancelOfferAccept = useCallback(
        async (offerId: string) => {
            await processAcceptance(
                offerId,
                RoundManagerApiService.Offers.cancelOfferAcceptAsync,
                false,
                i18n.t('SmartCargas.Rounds.ErrorCancellingOfferAcceptance'),
            );
        },
        [processAcceptance],
    );

    const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);

    const confirmOffer = useCallback(
        async (offerId: string) => {
            const { response, error, conflictData } =
                await RoundManagerApiService.Offers.confirmOfferAsync(offerId);

            if (response) {
                setConfirmationOffersList(prevItems => {
                    for (let i = Position.First; i <= Position.Third; i++) {
                        prevItems[i] = prevItems[i].map((item: IOffer) =>
                            item.loadOfferId === offerId
                                ? { ...item, confirmed: true }
                                : item,
                        );
                    }

                    return prevItems;
                });

                updateEffectedCounters(true);
                return;
            }

            if (error?.cause === 'CONFLICT' || conflictData) {
                setIsConflictModalOpen(true);
                return;
            }

            setToast(
                'negative',
                i18n.t('Erro'),
                i18n.t('SmartCargas.Rounds.ErrorOfferConfirmation'),
            );
        },
        [setToast, updateEffectedCounters],
    );

    const closeConflictModal = useCallback(() => {
        setIsConflictModalOpen(false);
        fetchFirstPageByPosition(Position.First);
    }, [fetchFirstPageByPosition]);

    const selectOffer = useCallback(
        (loadId: string, selected: boolean) => {
            if (selected && selectedOffersCount >= SELECTION_LIMIT) {
                setToast(
                    'warning',
                    i18n.t('Atencao'),
                    i18n.t('Loads.LimiteLoadsSeleconadas'),
                );
                return;
            }

            if (state.currentPhase === 'Acceptance')
                setAcceptanceOffersList(prevItems =>
                    prevItems.map((item: IOffer) =>
                        item.load.loadId === loadId
                            ? { ...item, checked: selected }
                            : item,
                    ),
                );

            if (state.currentPhase === 'Confirmation') {
                setConfirmationOffersList(prevItems => {
                    for (let i = Position.First; i <= Position.Third; i++) {
                        prevItems[i] = prevItems[i].map((item: IOffer) =>
                            item.load.loadId === loadId
                                ? { ...item, checked: selected }
                                : item,
                        );
                    }

                    return { ...prevItems };
                });
            }
        },
        [selectedOffersCount, setToast, state.currentPhase],
    );

    const handleNewOfferByCarrier = useCallback((payload: any) => {
        const rate =
            payload.ratesByVehicle?.find(
                (item: IOfferLoadRateByVehicle) => item.isAccepted,
            ) ?? payload.ratesByVehicle?.[0];
        const offer: IOffer = {
            loadOfferId: payload.id,
            load: {
                ...payload.loadData,
                ratesByVehicle: payload.ratesByVehicle ?? [],
                shipment: {
                    chargeAmount: rate?.chargeAmount,
                    ...payload.loadData.shipment,
                },
            },
            ...payload,
            insertedMidRound: true,
        };

        hydrateAcceptedOffersRates([offer]);

        if (offer.load.isPrioritized) {
            setAcceptanceOffersList(prevItems => [offer, ...prevItems]);
        } else {
            setAcceptanceOffersList(prevItems => {
                const prioritized = prevItems.filter(o => o.load.isPrioritized);
                const unprioritized = prevItems.filter(
                    o => !o.load.isPrioritized,
                );

                return [...prioritized, offer, ...unprioritized];
            });
        }

        availableCount.current += 1;
    }, [hydrateAcceptedOffersRates]);

    const handleRoundByCarrierNotification = useCallback(
        (notification: {
            type: string;
            country: string;
            loadId: string;
            status: string;
            carrierCode: string;
        }) => {
            if (notification.type === 'LoadConfirmed') {
                setConfirmationOffersList(prevItems => {
                    const updated = { ...prevItems };
                    for (let i = Position.First; i <= Position.Third; i++) {
                        updated[i] = prevItems[i].map((item: IOffer) => {
                            if (item.load.loadId === notification.loadId) {
                                if (!item.confirmed) {
                                    return {
                                        ...item,
                                        confirmed: true,
                                        confirmedBy: notification.carrierCode,
                                    };
                                }
                            }
                            return item;
                        });
                    }
                    return updated;
                });
            }
        },
        [],
    );

    useEffect(() => {
        if (isConnected && connection) {
            connection.on('NewOfferByCarrier', handleNewOfferByCarrier);
            connection.on('Notification', handleRoundByCarrierNotification);
        }

        return () => {
            if (connection) {
                connection.off('NewOfferByCarrier');
                connection.off('Notification');
            }
        };
    }, [
        isConnected,
        connection,
        handleNewOfferByCarrier,
        handleRoundByCarrierNotification,
    ]);

    useEffect(() => {
        const wasDisconnected =
            previousConnectionState.current === 'Reconnecting' ||
            previousConnectionState.current === 'Disconnected';
        const isNowConnected = connectionState === 'Connected';

        if (wasDisconnected && isNowConnected && state.currentPhase) {
            dispatch({ type: 'FETCH_PHASE_START' });
        }

        previousConnectionState.current = connectionState;
    }, [
        connectionState,
        state.currentPhase,
        fetchRoundInfo,
        fetchFirstPageByPosition,
    ]);

    useEffect(() => {
        const checkConfirmationAvailability = () => {
            const now = new Date();

            setIsConfirmationAvailable(s => ({
                ...s,
                [Position.First]: true,
            }));

            if (state.confirmation2ndBeginningTime) {
                const available2 =
                    now >= new Date(state.confirmation2ndBeginningTime);
                setIsConfirmationAvailable(s => ({
                    ...s,
                    [Position.Second]: available2,
                }));
            } else {
                setIsConfirmationAvailable(s => ({
                    ...s,
                    [Position.Second]: false,
                }));
            }

            if (state.confirmation3rdBeginningTime) {
                const available3 =
                    now >= new Date(state.confirmation3rdBeginningTime);
                setIsConfirmationAvailable(s => ({
                    ...s,
                    [Position.Third]: available3,
                }));
            } else {
                setIsConfirmationAvailable(s => ({
                    ...s,
                    [Position.Third]: false,
                }));
            }
        };

        if (state.currentPhase === 'Confirmation') {
            checkConfirmationAvailability();
            const interval = setInterval(checkConfirmationAvailability, 1000);
            return () => clearInterval(interval);
        }

        return () => {};
    }, [
        state.currentPhase,
        state.confirmation2ndBeginningTime,
        state.confirmation3rdBeginningTime,
    ]);

    useEffect(() => {
        if (state.status === 'idle' || state.status === 'transitioning')
            dispatch({ type: 'FETCH_PHASE_START' });

        if (state.status === 'loadingPhase') fetchRoundInfo();

        if (
            (state.status === 'loadingOffers' ||
                state.status === 'filtering') &&
            state.currentPhase === 'Acceptance'
        )
            fetchFirstPageByPosition(Position.First);

        if (state.status === 'filtering') {
            setAcceptanceOffersList(state.offers.acceptance.items);
            setConfirmationOffersList(state.offers.confirmation.items);
        }

        if (state.status === 'active') {
            if (state.currentPhase === 'Acceptance') {
                setAcceptanceOffersList(state.offers.acceptance.items);
            }

            if (state.currentPhase === 'Confirmation') {
                setConfirmationOffersList(state.offers.confirmation.items);
            }
        }

        if (state.status === 'prepareForTransition')
            setTimeout(() => {
                dispatch({ type: 'ROUND_END' });
                setAcceptedOffersRates({});
            }, 2000);
    }, [state, fetchRoundInfo, fetchFirstPageByPosition]);

    const provided = useMemo(
        () =>
            ({
                acceptanceOffersList,
                confirmationOffersList,
                availableCount,
                effectedCount,
                acceptOffer,
                cancelOfferAccept,
                confirmOffer,
                selectedOffersCount,
                selectOffer,
                fetchFirstPageByPosition,
                fetchNextPageByPosition,
                acceptanceHasMore,
                confirmationHasMore,
                roundError,
                acceptanceError,
                confirmationErrors,
                filters,
                isConflictModalOpen,
                closeConflictModal,
                isConfirmationAvailable,
                dispatch,
                roundState: state,
                acceptedOffersRates,
                setAcceptedOffersRates,
            }) as IOffersRoundContext,
        [
            acceptanceOffersList,
            confirmationOffersList,
            availableCount,
            effectedCount,
            acceptOffer,
            cancelOfferAccept,
            confirmOffer,
            selectedOffersCount,
            selectOffer,
            fetchFirstPageByPosition,
            fetchNextPageByPosition,
            acceptanceHasMore,
            confirmationHasMore,
            roundError,
            acceptanceError,
            confirmationErrors,
            filters,
            isConflictModalOpen,
            closeConflictModal,
            isConfirmationAvailable,
            dispatch,
            state,
            acceptedOffersRates,
            setAcceptedOffersRates,
        ],
    );

    return (
        <OffersRoundContext.Provider value={provided}>
            {children}
        </OffersRoundContext.Provider>
    );
}

export const useOffersRound = (): IOffersRoundContext =>
    useContext(OffersRoundContext);
